import { ResultNavigation, resultNavigationClearance } from './ResultNavigation';
import { SaveResultButton } from './SaveResultButton';
import type { ResultStep } from './resultStep';
import { useLeadPost } from './useLeadPost';

import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { ScrollView } from '@/components/ui/scroll-view';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { CommunityPostContent } from '@/features/inquiry-library';
import type { LibraryPost } from '@/features/inquiry-library/model';
import type { AskAvailableResult, AskResultPost } from '@/services/ask/api';
import type { AskSelection } from '@/services/stores/askUiStore';

function DetailMessage({ children }: { children: string }) {
  return (
    <VStack className="flex-1 items-center justify-center gap-2 p-6">
      <Text className="text-center text-sm text-typography-700">{children}</Text>
    </VStack>
  );
}

/**
 * One result's lead post in full, with a floating footer to step to the
 * turn's other results.
 *
 * Shared by every presentation of a Research result. The fallback sheet and
 * desktop pane (`ResultPosts` below) pin the save toggle in a bar above the
 * post, where the pushed result screen has it in its header, so
 * `showSaveButton` is false there. The bar keeps its height while the post
 * loads, so the content below does not jump when the toggle appears.
 *
 * Only the scroll view is keyed to the post, so each result opens at its top
 * while the footer stays mounted across steps. Remounting the footer would
 * drop keyboard and screen-reader focus from the button just pressed.
 *
 * `bottomInset` is the safe-area inset the host has not already padded, so
 * the footer clears the home indicator on the pushed screen without doubling
 * the padding a sheet already applies.
 */
export function ResultPostBody({
  post,
  data,
  isPending,
  isError,
  onRetry,
  showSaveButton,
  step,
  bottomInset = 0,
}: {
  post: AskResultPost | undefined;
  data: LibraryPost | undefined;
  isPending: boolean;
  isError: boolean;
  onRetry: () => void;
  showSaveButton: boolean;
  step: ResultStep | null;
  bottomInset?: number;
}) {
  if (!post) {
    return <DetailMessage>This result is no longer available.</DetailMessage>;
  }

  const renderPost = () => {
    if (isPending) {
      return (
        <VStack className="items-center justify-center p-6">
          <Spinner accessibilityLabel="Loading the inquiry" />
        </VStack>
      );
    }

    if (isError || !data) {
      return (
        <VStack className="items-center justify-center gap-3 p-6">
          <Text className="text-center text-sm text-typography-700">
            This inquiry couldn&apos;t be loaded. It may no longer be available.
          </Text>

          <Button size="sm" variant="outline" action="secondary" onPress={onRetry}>
            <ButtonText>Try again</ButtonText>
          </Button>
        </VStack>
      );
    }

    return <CommunityPostContent post={data} source="library_chat" />;
  };

  const hasNavigation = step != null && step.count > 1;

  const clearance = resultNavigationClearance(bottomInset);

  return (
    <View className="flex-1">
      {showSaveButton ? (
        <HStack className="h-10 items-center justify-end px-3">
          {data ? <SaveResultButton post={data} /> : null}
        </HStack>
      ) : null}

      <ScrollView
        key={post.id}
        className="flex-1"
        contentContainerClassName="gap-5 p-5"
        contentContainerStyle={hasNavigation ? { paddingBottom: clearance } : undefined}
        scrollIndicatorInsets={hasNavigation ? { bottom: clearance } : undefined}>
        {renderPost()}
      </ScrollView>

      {hasNavigation ? <ResultNavigation step={step} bottomInset={bottomInset} /> : null}
    </View>
  );
}

/**
 * The Android/web sheet and desktop pane's presentation of a selected result.
 *
 * The Ask response names posts and never carries their bodies, so the open
 * post is read from the community post route, which applies the reader's own
 * read or preview access.
 */
function ResultPosts({ result, step }: { result: AskAvailableResult; step: ResultStep | null }) {
  const { post, data, isPending, isError, refetch } = useLeadPost(result);

  return (
    <ResultPostBody
      post={post}
      data={data}
      isPending={isPending}
      isError={isError}
      onRetry={refetch}
      showSaveButton
      step={step}
    />
  );
}

/**
 * The selected result, shown through its lead published Inquiry Library post.
 * Nothing from the unreviewed thread behind the question appears here.
 */
export function RelatedInquiryDetail({
  selection,
  result,
  hasResults,
  step = null,
}: {
  selection: AskSelection | null;
  result: AskAvailableResult | null;
  hasResults: boolean;
  step?: ResultStep | null;
}) {
  if (!selection) {
    return (
      <DetailMessage>
        {hasResults
          ? 'Select a result to read its inquiry from the Inquiry Library.'
          : 'When a question finds related inquiries, the one you choose opens here.'}
      </DetailMessage>
    );
  }

  if (selection.source !== 'inquiry') {
    return (
      <DetailMessage>This result can&apos;t be opened in this version of the app.</DetailMessage>
    );
  }

  if (!result) {
    return <DetailMessage>This result is no longer available.</DetailMessage>;
  }

  return <ResultPosts result={result} step={step} />;
}
